import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  CircularProgress,
  styled,
} from "@mui/material";
import Pusher from "pusher-js";
import { BatchLogsAPI } from "../../../../api/Endpoints/BatchLogs";

// Custom styled progress bar with green and red sections
const StyledLinearProgress = styled(LinearProgress)(({ theme, successPercentage, failedPercentage }) => ({
  height: 10,
  borderRadius: 5,
  backgroundColor: '#e0e0e0',
  '& .MuiLinearProgress-bar': {
    borderRadius: 5,
  },
  '& .MuiLinearProgress-bar1Determinate': {
    backgroundColor: '#4caf50', // Green for success
  },
  '& .MuiLinearProgress-bar2Determinate': {
    backgroundColor: '#f44336', // Red for failed
  },
}));

const ProgressBox = ({ open, onClose }) => {
  const [batchData, setBatchData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let interval;
    
    if (open) {
      // Check if there's saved batch data
      const savedBatchState = localStorage.getItem('bulkMessageBatchState');
      if (savedBatchState) {
        const batchState = JSON.parse(savedBatchState);
        setBatchData(batchState);
        setLoading(false);
      }
      
      // Initial fetch
      fetchLatestBatch();
      
      // Set up Pusher listener
      const pusher = new Pusher("e9821283ae4e0a5b1271", {
        cluster: "ap2",
      });
      
      const channel = pusher.subscribe("desai-channel");
      channel.bind("desai-event", (data) => {
        // When we receive a Pusher event, fetch the latest batch data
        fetchLatestBatch();
      });

      // Set up polling as a fallback
      interval = setInterval(fetchLatestBatch, 5000);

      // Cleanup Pusher connection and interval on unmount
      return () => {
        pusher.unsubscribe("desai-channel");
        pusher.disconnect();
        if (interval) clearInterval(interval);
      };
    }
  }, [open]);

  const fetchLatestBatch = async () => {
    try {
      const response = await BatchLogsAPI.getBulkMessage();
      
      // Only update if we have data
      if (response.data) {
        setBatchData(response.data);
        setLoading(false);
        
        // Save batch state to localStorage
        localStorage.setItem('bulkMessageBatchState', JSON.stringify(response.data));
        
        // Close the progress box if the batch is completed or failed
        if (response.data.status === "completed" || response.data.status === "failed") {
          // Add a small delay before closing to show the final state
          setTimeout(() => {
            onClose();
          }, 5000); // Close after 5 seconds
        }
      }
    } catch (error) {
      console.error("Error fetching batch data:", error);
    }
  };

  // Calculate progress percentage
  const calculateProgress = () => {
    if (!batchData) return 0;
    
    const total = batchData.total_messages || 1;
    const processed = (batchData.sent_count || 0) + (batchData.failed_count || 0);
    
    return Math.min(100, Math.round((processed / total) * 100));
  };

  // Calculate success percentage
  const calculateSuccessPercentage = () => {
    if (!batchData || !batchData.total_messages) return 0;
    
    return Math.round(((batchData.sent_count || 0) / batchData.total_messages) * 100);
  };

  // Calculate failed percentage
  const calculateFailedPercentage = () => {
    if (!batchData || !batchData.total_messages) return 0;
    
    return Math.round(((batchData.failed_count || 0) / batchData.total_messages) * 100);
  };

  // Determine if the process is completed
  const isCompleted = batchData?.status === "completed";
  const isFailed = batchData?.status === "failed";
  const isInProgress = batchData?.status === "processing" || batchData?.status === "pending";

  if (!open) return null;

  const successPercentage = calculateSuccessPercentage();
  const failedPercentage = calculateFailedPercentage();

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h6" gutterBottom>
        Bulk Message Progress
      </Typography>
      
      {loading && !batchData ? (
        <Box sx={{ display: "flex", alignItems: "center", py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Initializing progress tracking...
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography variant="body2" color="textSecondary" sx={{ flexGrow: 1 }}>
              Batch ID: {batchData?.batch_code || "Waiting for batch..."}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {batchData?.sent_count || 0}/{batchData?.total_messages || 0} processed
            </Typography>
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <StyledLinearProgress 
              variant="buffer"
              value={successPercentage}
              valueBuffer={successPercentage + failedPercentage}
              successPercentage={successPercentage}
              failedPercentage={failedPercentage}
            />
          </Box>
          
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: "green",
                  mr: 1,
                }}
              />
              <Typography variant="body2">
                Success: {batchData?.sent_count || 0} ({successPercentage}%)
              </Typography>
            </Box>
            
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: "red",
                  mr: 1,
                }}
              />
              <Typography variant="body2">
                Failed: {batchData?.failed_count || 0} ({failedPercentage}%)
              </Typography>
            </Box>
          </Box>
          
          {isCompleted && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1, textAlign: "center", fontWeight: 'bold' }}>
              Batch processing completed successfully!
            </Typography>
          )}
          
          {isFailed && (
            <Typography variant="body2" color="error.main" sx={{ mt: 1, textAlign: "center", fontWeight: 'bold' }}>
              Batch processing completed with failures.
            </Typography>
          )}
          
          {!batchData && !loading && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1, textAlign: "center" }}>
              Waiting for message sending to start...
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default ProgressBox;